import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Download as DownloadIcon } from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, isSameMonth, startOfDay, endOfDay } from 'date-fns';
import axios from 'axios';
import config from '../config';

const RuleBasedReports = () => {
  const [selectedAgent, setSelectedAgent] = useState('');
  // Initialize with current month, but allow any date selection
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));
  const [agents, setAgents] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [ruleDefinitions, setRuleDefinitions] = useState([]);

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem('user'));
    setUser(currentUser);
    
    if (currentUser && currentUser.role === 'admin') {
      loadAgents();
    } else {
      window.location.href = '/login';
    }
  }, []);

  useEffect(() => {
    if (selectedAgent && startDate && endDate) {
      loadReports();
    } else {
      setReports([]);
    }
  }, [selectedAgent, startDate, endDate]);

  const loadAgents = async () => {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/rule-based-reports/agents`);
      setAgents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading agents:', error);
      setError('Failed to load agents');
    }
  };

  const loadReports = async () => {
    if (!selectedAgent || !startDate || !endDate) {
      return;
    }

    // Validate same month
    if (!isSameMonth(startDate, endDate)) {
      setError('Start date and end date must be in the same month');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams({
        agentCustomId: selectedAgent,
        startDate: format(startOfDay(startDate), 'yyyy-MM-dd'),
        endDate: format(endOfDay(endDate), 'yyyy-MM-dd')
      });

      const response = await axios.get(`${config.apiBaseUrl}/api/rule-based-reports/reports?${params}`);
      
      if (response.data.success) {
        setReports(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to load reports');
      }
    } catch (error) {
      console.error('Error loading reports:', error);
      setError(error.response?.data?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleStartDateChange = (newDate) => {
    if (!newDate) return;
    
    // Set start date to the selected date (e.g., Oct 10)
    setStartDate(newDate);
    
    // Automatically set end date to the end of the selected month (e.g., Oct 31)
    const monthEnd = endOfMonth(newDate);
    setEndDate(monthEnd);
    
    // Clear any previous errors
    setError('');
  };

  const handleEndDateChange = (newDate) => {
    if (!newDate) return;
    
    // Validate same month
    if (!isSameMonth(newDate, startDate)) {
      setError('End date must be in the same month as start date');
      return;
    }
    
    if (newDate < startDate) {
      setError('End date must be after start date');
      return;
    }
    
    setError('');
    setEndDate(newDate);
  };

  const handleAgentChange = (event) => {
    setSelectedAgent(event.target.value);
  };

  // Load rule definitions from rule-definition collection
  useEffect(() => {
    const loadRuleDefinitions = async () => {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/api/rule-definitions`, {
          params: { isActive: 'true' }
        });
        setRuleDefinitions(response.data || []);
      } catch (error) {
        console.error('Error loading rule definitions:', error);
      }
    };
    loadRuleDefinitions();
  }, []);

  // Get all rule names from rule-definition collection (not from reports)
  const getAllRuleNames = () => {
    return ruleDefinitions.map(rule => rule.name).sort();
  };

  const downloadExcel = () => {
    if (reports.length === 0) {
      setError('No data to export');
      return;
    }

    // Get selected agent name
    const agent = agents.find(a => a.customAgentId === selectedAgent);
    const agentName = agent ? agent.name : selectedAgent;

    // Get all rule names dynamically
    const ruleNames = getAllRuleNames();

    // Create CSV content
    const headers = ['Date', ...ruleNames];

    const rows = reports.map(report => {
      const row = [format(new Date(report.date), 'yyyy-MM-dd')];
      ruleNames.forEach(ruleName => {
        const outcomes = report.outcomes || {};
        row.push(outcomes[ruleName] || 0);
      });
      return row;
    });

    // Create CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Add BOM for UTF-8
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `rule-based-reports-${agentName}-${format(startDate, 'yyyy-MM')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Only show for admin
  if (user && user.role !== 'admin') {
    return null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Agent</InputLabel>
                <Select
                  value={selectedAgent}
                  label="Agent"
                  onChange={handleAgentChange}
                >
                  {agents.map((agent) => (
                    <MenuItem key={agent.customAgentId} value={agent.customAgentId}>
                      {agent.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
                maxDate={new Date()} // Can't select future dates
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
                shouldDisableDate={(date) => {
                  // Disable dates outside the selected month
                  if (!isSameMonth(date, startDate)) {
                    return true;
                  }
                  // Disable dates before start date
                  if (date < startDate) {
                    return true;
                  }
                  return false;
                }}
                minDate={startDate}
                maxDate={endOfMonth(startDate)}
              />
            </Grid>
          </Grid>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && reports.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Report Data ({reports.length} days)
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={downloadExcel}
                >
                  Download Excel
                </Button>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      {getAllRuleNames().map(ruleName => (
                        <TableCell key={ruleName} align="right">{ruleName}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports.map((report, index) => {
                      const outcomes = report.outcomes || {};
                      const ruleNames = getAllRuleNames();
                      return (
                        <TableRow key={index}>
                          <TableCell>{format(new Date(report.date), 'yyyy-MM-dd')}</TableCell>
                          {ruleNames.map(ruleName => (
                            <TableCell key={ruleName} align="right">
                              {outcomes[ruleName] || 0}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {!loading && selectedAgent && reports.length === 0 && (
            <Alert severity="info">
              No reports found for the selected agent and date range.
            </Alert>
          )}

          {!selectedAgent && (
            <Alert severity="info">
              Please select an agent to view reports.
            </Alert>
          )}
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default RuleBasedReports;

